import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
  CutTag,
  DataMatchOutput,
  DataMatchSearchMode,
  DataMatchSelection,
  DataMatchStatus,
  FileStatus,
  MetricName,
  MetricNamespace,
  Stepper,
  TestFileCheck,
  TextBlock,
  TextcutRectangle,
  UndoEvent,
} from '../../types'
import { countBy, map } from 'lodash'
import {
  createDataMatchBindings,
  disableEvents,
  enableEvents,
  generateExcelColumns,
  getActiveRange,
  getActiveWorksheet,
  getColumnIndex,
  getDataMatchOutputRanges,
  getRangeByWorksheet,
  lettersToNumber,
  loadTextFromRange,
  numberToLetters,
} from '../../workbook'
import { ClockLoader } from 'react-spinners'
import { nanoid } from 'nanoid'
import {
  calculateHeightandWidth,
  findTOpLeftPointFromVertices,
} from '../../utils/spatial'
import { useAppDispatch, useAppSelector } from '../../dispatch'
import { toast } from 'react-toastify'
import { addMultipleReferences } from '../../slice/referenceSlice'
import CustomDisclosure from '../CustomDisclosure'
import { pushToUndoStack } from '../../slice/undoSlice'
import { getFileStructure, getOfficeErrorTitle } from '../../utils/common'
import { isOfficeError, isTextBlock } from '../../utils/guards'
import { NO_OUTPUT } from '../../constant'
import { useNavigate } from 'react-router-dom'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react'
import DataMatchOutputDropdown from '../DataMatchOutputDropdown'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import { dataMatchWithDuplicateHandling } from '../../utils/ocr'
import TextcutRectangleBuilder from '../../utils/TextcutRectangleBuilder'
import { addMetric } from '../../utils/metrics'
import InputRangeElem from './InputRangeElem'
import FilesElem from './FilesElem'
import OutputElem from './OutputElem'
import {
  formatDataMatchResultWithDuplicateHandling,
  getDataMatchFolderStruct,
  getInputFiles,
  processLargeNumberValueAsText,
} from '../../utils/data-match-utils'
import { ResultAsync } from 'neverthrow'
import { errorStringify } from '../../utils/error'
import {
  useDataMatchPageState,
  withDataMatchPageStateContext,
} from '../../context/DataMatchPageStateContext'
import mittEmitter from '../../utils/mitt'
import useSuccessFilesAndFolders from '../../hooks/useSuccessFilesAndFolders'
import { isLargeNumber } from '../../utils/currency'

const columnHelper = createColumnHelper<DataMatchSelection>()

const dataMatchInputOutputOverlapCheck = async (
  input: string,
  output: string[]
) => {
  const set = new Set(output)
  const noOutputCount = output.reduce((a, b) => {
    if (b === NO_OUTPUT) {
      return a + 1
    }
    return a
  }, 0)
  const setSize = output.includes(NO_OUTPUT)
    ? set.size + noOutputCount - 1
    : set.size + noOutputCount
  if (setSize !== output.length)
    return {
      pass: false,
      message: 'Duplicated output range',
    }
  const arr = await getColumnIndex(input, 0)
  if (arr?.some((val) => output.includes(val))) {
    return {
      pass: false,
      message: 'Overlapped input and output ',
    }
  }
  return {
    pass: true,
    message: '',
  }
}

const createDataMatchTextcutRectangle = (
  range: Excel.Range,
  block: TextBlock,
  sheetId: string,
  sheetName: string,
  values: any,
  bindingGroupId: string | null = null,
  fileName: string | null = null
) => {
  if (!block.fileId || !block.filePage || !block.pageH || !block.pageW)
    return null
  const nano = nanoid()
  const vs = block.boundingPoly.vertices
  const topLeft = findTOpLeftPointFromVertices(vs)
  const [h, w] = calculateHeightandWidth(vs[0], vs[1], vs[2], vs[3])
  const builder = new TextcutRectangleBuilder()
  const top = topLeft?.y && block.pageH ? (topLeft?.y / block.pageH) * 100 : 0
  builder
    .addBoudingInfo({
      x: topLeft?.x ?? 0,
      y: topLeft?.y ?? 0,
      h,
      w,
    })
    .addSheetInfo({
      range: range.address.split('!')[1],
      sheetId: sheetId,
      sheetName: sheetName,
    })
    .addFileInfo({
      fileId: block.fileId as string,
      filePage: block.filePage!,
    })
    .addOcrInfo({ height: 0, id: '', width: 0 })
    .addStyle({ cellColor: '#C3E9FE', fill: '', stroke: '#A9E0FE' })
    .addBindingId(nano)
    .addDegee(0)
    .addTag(CutTag.DATA_MATCH)
    .addValues([[values]])
    .addTop(top)
  if (bindingGroupId) {
    builder.addBindingGroupId(bindingGroupId)
  }
  if (fileName) {
    builder.addFileName(fileName)
  }
  return builder.build()
}

const DataMatching = () => {
  const { state: dataMatchPageState, dispatch: dispatchDataMatchPageState } =
    useDataMatchPageState()
  const [loading, setLoading] = useState(false)
  const {
    files: data,
    isError,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useSuccessFilesAndFolders()
  const navigate = useNavigate()
  const [files, setFiles] = useState<TestFileCheck[]>([])
  const inputRangeRef = useRef<HTMLInputElement>(null)
  const dataMatchIsCanceled = useRef(false)

  // derived states
  const isLocalMode = useAppSelector((state) => state.localMode.isLocalMode)
  const dispatchDataMatchPageStateRef = useRef(dispatchDataMatchPageState)
  const firstRowHeader = dataMatchPageState.firstRowHeader
  const inputRange = dataMatchPageState.inputRange
  const outputRange = dataMatchPageState.outputRange
  const primaryColumnOptions = dataMatchPageState.primaryColumnOptions
  const primaryIndices = dataMatchPageState.primaryIndices
  const stepper = dataMatchPageState.stepper
  const inputRangeFocus = dataMatchPageState.inputRangeFocus
  const allFilesSelected = files.length > 0 && files.every((f) => f.checked)
  const tableData = dataMatchPageState.tableData
  const searchMode = dataMatchPageState.searchMode
  const folders =
    data && data.length ? data.filter((d) => d.type === 'FOLDER') : []
  const fileStructure = getFileStructure(folders, files)
  const foldersAreAllEmpty = data
    .filter((d) => d.type === 'FILE')
    .every((file) => !file.parentFolderId)
  const selectedFolders = dataMatchPageState.selectedFolders
  const duplicatedColumns = dataMatchPageState.duplicatedColumns
  const outputTableError = dataMatchPageState.outputTableError

  const setFirstRowHeader = (value: 0 | 1) =>
    dispatchDataMatchPageState({ type: 'SET_FIRST_ROW_HEADER', payload: value })

  const setStepper = (value: Stepper) =>
    dispatchDataMatchPageState({ type: 'SET_STEP', payload: value })

  const setInputRangeFocus = (focus: boolean) => {
    dispatchDataMatchPageState({
      type: 'SET_INPUT_RANGE_FOCUS',
      payload: focus,
    })
  }

  const setSelectedFolders = (folders: string[]) =>
    dispatchDataMatchPageStateRef.current({
      type: 'SET_SELECTED_FOLDERS',
      payload: folders,
    })

  const setSearchMode = (mode: DataMatchSearchMode) =>
    dispatchDataMatchPageStateRef.current({
      type: 'SET_SEARCH_MODE',
      payload: mode,
    })

  const setOutputTableError = (isError: boolean) =>
    dispatchDataMatchPageStateRef.current({
      type: 'SET_OUTPUT_TABLE_ERROR',
      payload: isError,
    })

  const onCompulsoryCheckboxChange =
    (info: CellContext<DataMatchSelection, string>) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = info.getValue()
      const index = primaryColumnOptions.findIndex((option) =>
        value.startsWith(option)
      )
      if (index === -1) return
      if (primaryIndices.includes(index)) {
        const filtered = primaryIndices.filter((val) => val !== index)
        dispatchDataMatchPageStateRef.current({
          type: 'SET_PRIMARY_INDICES',
          payload: filtered,
        })
      } else {
        dispatchDataMatchPageStateRef.current({
          type: 'SET_PRIMARY_INDICES',
          payload: [...primaryIndices, index],
        })
      }
    }

  const InputColumn = ({
    info,
  }: {
    info: CellContext<DataMatchSelection, string>
  }) => {
    const value = info.getValue()
    return (
      <span
        className={`relative flex  justify-between items-center  h-max w-full p-2 ${
          duplicatedColumns.includes(value) ? 'bg-red-100 text-red-700' : ''
        }`}
      >
        <span>{value}</span>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
          as="span"
          show={true}
          className={`flex h-max`}
        >
          <Popover className="relative">
            <PopoverButton
              className={
                'hover:bg-gray-200 transition ease-in-out delay-75 hover:scale-110 rounded'
              }
            >
              <MoreHorizOutlinedIcon />
            </PopoverButton>

            <PopoverPanel className="absolute z-10 bg-white border rounded w-40">
              <div className="flex items-center p-2">
                <input
                  checked={primaryIndices.some((index) =>
                    info
                      .getValue()
                      .startsWith(primaryColumnOptions.at(index) ?? '@@@')
                  )}
                  type="checkbox"
                  onChange={onCompulsoryCheckboxChange(info)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label className="ms-2 text-sm font-medium text-gray-900  w-full ">
                  <p>Must find this data</p>
                </label>
              </div>
            </PopoverPanel>
          </Popover>
        </Transition>
      </span>
    )
  }

  const columns = selectedFolders.length
    ? ([
        columnHelper.accessor('input', {
          cell: (info) => {
            return <InputColumn info={info} />
          },
          header: 'Input Columns',
        }),
        ...selectedFolders
          .map((folderId) => {
            const folder = folders.find((folder) => folder.fileId === folderId)
            if (!folder) return null
            return columnHelper.accessor(folderId, {
              cell: (info) => info.getValue(),
              header: folder.folderName,
              id: folderId,
            })
          })
          .filter((val) => val !== null),
      ] as ColumnDef<DataMatchSelection, any>[])
    : [
        columnHelper.accessor('input', {
          cell: (info) => {
            return <InputColumn info={info} />
          },
          header: 'Input Columns',
        }),
        columnHelper.accessor('output', {
          cell: (info) => info.getValue(),
          header: 'Output Columns',
        }),
      ]

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const dispatch = useAppDispatch()

  // initialize input range from the active selection
  useEffect(() => {
    let ignore = false
    const func = async () => {
      const range = await getActiveRange()
      if (ignore) return
      const arr = range.split('!')
      if (arr.length !== 2) throw new Error('Invalid range format')

      dispatchDataMatchPageStateRef.current({
        type: 'SET_INPUT_RANGE',
        payload: arr[1],
      })
    }
    func().catch((err) => {
      if (ignore) return
      console.error(err)
      toast.error(`Initializing input range failed: ${err}`)
    })

    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    if (data.length) {
      // setFolders(data.filter((d) => d.type === 'FOLDER'))
      const arr: TestFileCheck[] = data
        .filter((file) => file.type !== 'FOLDER')
        .filter((file) => file.status === FileStatus.SUCCEEDED)
        .map((file) => ({
          filename: file.fileName ?? '',
          checked: false,
          id: file.fileId,
          ocrPath: file.ocrFileKey,
          path: file.ocrFileKey,
          parentFolderId: file.parentFolderId,
        }))
      setFiles(arr)
    }
  }, [data])

  useEffect(() => {
    const func = async () =>
      Excel.run(async (ctx) => {
        const sheet = ctx.workbook.worksheets.getActiveWorksheet()
        if (inputRange === '') return []
        const range = sheet.getRange(inputRange)
        range.load(['columnCount', 'columnIndex', 'isNullObject'])
        await ctx.sync()
        if (range.isNullObject) return []
        const arr: string[] = []
        for (
          let i = range.columnIndex;
          i < range.columnIndex + range.columnCount;
          i++
        ) {
          arr.push(numberToLetters(i))
        }
        return arr
      })

    func()
      .then((res) => {
        dispatchDataMatchPageStateRef.current({
          type: 'BATCH',
          payload: {
            primaryIndices: [],
            primaryColumnOptions: res,
          },
        })
      })
      .catch((err) => {
        console.error(err)
        toast.error(err)
        dispatchDataMatchPageStateRef.current({
          type: 'SET_PRIMARY_COLUMN_OPTIONS',
          payload: [],
        })
      })
  }, [inputRange])

  useEffect(() => {
    const handler = (e: string) => {
      try {
        const args: Excel.WorksheetSelectionChangedEventArgs = JSON.parse(e)
        if (inputRangeFocus && inputRangeRef.current) {
          dispatchDataMatchPageStateRef.current({
            type: 'SET_INPUT_RANGE',
            payload: args.address,
          })
          inputRangeRef.current.focus()
        }
      } catch (err) {
        toast.error(`Error: ${err}`)
      }
    }

    mittEmitter.on('OnExcelWorkbooksSelectionChange', handler)

    return () => mittEmitter.off('OnExcelWorkbooksSelectionChange', handler)
  }, [inputRangeFocus])

  const getInitialOutputCols = useCallback(
    async (noOutput = false) => {
      const columns = await getColumnIndex(inputRange, firstRowHeader)
      if (!columns) return
      const num = columns.length
        ? lettersToNumber(columns[columns.length - 1][0])
        : -1
      if (selectedFolders.length > 0) {
        const arr: string[] = []
        for (let i = 0; i < selectedFolders.length * columns.length; i++) {
          arr.push(numberToLetters(num + i))
        }
        dispatchDataMatchPageStateRef.current({
          type: 'SET_OUTPUT_RANGE',
          payload: arr,
        })
      } else {
        const arr = columns.map((_, i) =>
          noOutput ? NO_OUTPUT : numberToLetters(num + i)
        )
        dispatchDataMatchPageStateRef.current({
          type: 'SET_OUTPUT_RANGE',
          payload: arr,
        })
      }
    },
    [firstRowHeader, inputRange, selectedFolders]
  )

  useEffect(() => {
    getInitialOutputCols().catch((err) => console.error(err))
  }, [getInitialOutputCols])

  useEffect(() => {
    const func = () => getColumnIndex(inputRange, firstRowHeader)

    func()
      .then((columns) => {
        if (!columns) return
        const setSelected = (index: number) => (val: string) => {
          const output = [...outputRange]
          output[index] = val
          dispatchDataMatchPageStateRef.current({
            type: 'SET_OUTPUT_RANGE',
            payload: output,
          })
        }
        if (selectedFolders.length) {
          const arr: DataMatchSelection[] = columns.map((index, i) => {
            return { input: index }
          })
          let count = 0
          for (let col = 0; col < selectedFolders.length; col++) {
            for (let row = 0; row < columns.length; row++) {
              arr[row][selectedFolders[col]] = (
                <DataMatchOutputDropdown
                  data={generateExcelColumns()}
                  index={count}
                  selected={outputRange.at(count) || NO_OUTPUT}
                  setSelected={setSelected(count)}
                  isDuplicate={
                    outputRange.at(count)
                      ? duplicatedColumns.includes(outputRange.at(count)!)
                      : false
                  }
                />
              )
              count++
            }
          }
          dispatchDataMatchPageStateRef.current({
            type: 'SET_TABLE_DATA',
            payload: arr,
          })
        } else {
          const arr: DataMatchSelection[] = columns.map((index, i) => {
            return {
              input: index,
              output: (
                <DataMatchOutputDropdown
                  data={generateExcelColumns()}
                  index={i}
                  selected={outputRange.at(i) || NO_OUTPUT}
                  setSelected={setSelected(i)}
                  isDuplicate={
                    outputRange.at(i)
                      ? duplicatedColumns.includes(outputRange.at(i)!)
                      : false
                  }
                />
              ),
            }
          })
          dispatchDataMatchPageStateRef.current({
            type: 'SET_TABLE_DATA',
            payload: arr,
          })
        }
      })
      .catch((err) => console.error(err))
  }, [
    inputRange,
    firstRowHeader,
    outputRange,
    selectedFolders,
    duplicatedColumns,
  ])

  useEffect(() => {
    const arr = outputRange.filter((r) => r !== NO_OUTPUT)
    const set = new Set(arr)
    if (
      set.size !== arr.length ||
      [...set.values()].some((val) => inputRange.includes(val))
    ) {
      const countObj = countBy(arr)
      const newDuplicatedColumns = new Set<string>()
      for (const k of Object.keys(countObj)) {
        if (countObj[k] > 1) {
          newDuplicatedColumns.add(k)
        }
      }
      for (const k of set) {
        if (inputRange.includes(k)) {
          newDuplicatedColumns.add(k)
        }
      }
      dispatchDataMatchPageStateRef.current({
        type: 'BATCH',
        payload: {
          outputTableError: true,
          duplicatedColumns: [...newDuplicatedColumns],
        },
      })
    } else {
      dispatchDataMatchPageStateRef.current({
        type: 'BATCH',
        payload: {
          outputTableError: false,
          duplicatedColumns: [],
        },
      })
    }
  }, [outputRange, inputRange])

  const onCheckAllFilesChange = () => {
    const arr = files.map((file) => ({
      ...file,
      checked: allFilesSelected ? false : true,
    }))
    setFiles(arr)
  }

  const uncheckAllFiles = () => {
    const arr = files.map((file) => ({ ...file, checked: false }))
    setFiles(arr)
  }

  const onSingleFileCheck = (id: string) => () => {
    const newArr = map(files, (f) =>
      f.id === id ? { ...f, checked: !f.checked } : f
    )
    setFiles(newArr)
  }

  const onInputRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value.substring(2, event.target.value.length)
    dispatchDataMatchPageStateRef.current({
      type: 'SET_INPUT_RANGE',
      payload: val,
    })
    if (val)
      selectRange(val).catch((err) => {
        console.error(err)
        console.error('val:', val)
      })
  }

  const dispatchPushToUndoStack = (
    preValues: any[][],
    reference: TextcutRectangle,
    type: 'ADD' | 'DELETE',
    references: TextcutRectangle[] = []
  ) => {
    const event: UndoEvent = {
      id: nanoid(),
      preValues,
      reference,
      type,
      references,
    }
    dispatch(pushToUndoStack(event))
  }

  // const getDataMatchReferenceRectsV2 = ({
  //   formattedResult,
  //   outputRanges,
  //   worksheet,
  //   result,
  // }: {
  //   outputRanges: Excel.Range[][]
  //   formattedResult: any[][]
  //   worksheet: Excel.Worksheet
  //   result: (string | TextBlock | null)[][]
  // }) => {
  //   const rects: TextcutRectangle[] = []
  //   for (let i = 0; i < formattedResult.length; i++) {
  //     const findingsOfTheRow = result[i]
  //     const rangesOfTheRow = outputRanges[i]
  //     for (let j = 0; j < rangesOfTheRow.length; j++) {
  //       const finding = findingsOfTheRow[j]
  //       if (finding == null || !isTextBlock(finding) || !('fileId' in finding))
  //         continue
  //       const range = rangesOfTheRow[j]
  //       const elem = createDataMatchTextcutRectangle(
  //         range,
  //         finding,
  //         worksheet.id,
  //         worksheet.name,
  //         formattedResult[i][j]
  //       )
  //       if (elem) {
  //         rects.push(elem)
  //         dispatchPushToUndoStack(range.values, elem, 'ADD')
  //       }
  //     }
  //   }
  //   return rects
  // }

  const getDataMatchReferenceRectsV1 = ({
    formattedResult,
    outputRanges,
    worksheet,
    result,
    textResult,
  }: {
    outputRanges: Excel.Range[][]
    formattedResult: any[][]
    worksheet: Excel.Worksheet
    result: DataMatchOutput
    textResult: any[][] | undefined
  }) => {
    const rects: TextcutRectangle[] = []
    for (let i = 0; i < formattedResult.length; i++) {
      const findingsOfTheRow = result[i]
      const rangesOfTheRow = outputRanges[i]
      for (let j = 0; j < rangesOfTheRow.length; j++) {
        if (
          formattedResult[i][j] === '' ||
          formattedResult[i][j] === DataMatchStatus.NOT_FOUND
        )
          continue
        else if (formattedResult[i][j] === DataMatchStatus.DUPLICATE) {
          const bindingGroupId = nanoid()
          const findings = findingsOfTheRow.map((f) => f[j])
          const elems: TextcutRectangle[] = []
          for (const finding of findings) {
            if (
              finding == null ||
              !isTextBlock(finding) ||
              !('fileId' in finding) ||
              !('filePage' in finding)
            )
              continue
            const f = files.find((file) => file.id === finding.fileId)
            if (!f) continue
            const range = rangesOfTheRow[j]
            const elem = createDataMatchTextcutRectangle(
              range,
              finding,
              worksheet.id,
              worksheet.name,
              textResult ? textResult[i][j] : formattedResult[i][j],
              bindingGroupId,
              f.filename
            )
            if (elem) {
              rects.push(elem)
              elems.push(elem)
            }
          }

          if (elems.length) {
            dispatchPushToUndoStack(
              rangesOfTheRow[j].values,
              elems[0],
              'ADD',
              elems
            )
          }
        } else {
          const finding = findingsOfTheRow[0][j]
          if (
            finding == null ||
            !isTextBlock(finding) ||
            !('fileId' in finding)
          )
            continue
          const range = rangesOfTheRow[j]
          const elem = createDataMatchTextcutRectangle(
            range,
            finding,
            worksheet.id,
            worksheet.name,
            formattedResult[i][j]
          )
          if (elem) {
            rects.push(elem)
            dispatchPushToUndoStack(range.values, elem, 'ADD')
          }
        }
      }
    }
    return rects
  }

  const setOuputRangeStyle = (range: Excel.Range, value: any) => {
    if (isLargeNumber(value)) {
      range.numberFormat = [['@']]
    }
    range.values = [[value]]
    range.format.horizontalAlignment = Excel.HorizontalAlignment.right
    range.format.autofitColumns()
    range.format.font.name = 'Segoe UI'
    range.format.fill.color = '#C3E9FE'
  }

  // const createDataMatchBinding = (
  //   ctx: Excel.RequestContext,
  //   rects: TextcutRectangle[],
  //   sheet: Excel.Worksheet
  // ) => {
  //   const bindingSet = new Set<string>()
  //   for (const elem of rects) {
  //     if (elem.bindingGroupId && bindingSet.has(elem.bindingGroupId)) continue
  //     const range = sheet.getRange(elem.rangeAddr)
  //     createBindingAndAddOnSelectionHandler(ctx, range, 'Range', elem.bindingId)
  //     if (elem.bindingGroupId) {
  //       bindingSet.add(elem.bindingGroupId)
  //     }
  //   }
  // }

  const onMatchDocumentClick = () => {
    dataMatchIsCanceled.current = false
    const func = async () => {
      setLoading(true)
      const inputValuesResult = await ResultAsync.fromPromise(
        loadTextFromRange(inputRange, outputRange),
        (error) =>
          new Error(`Failed to get input, error: ${errorStringify(error)}`)
      )

      if (inputValuesResult.isErr()) {
        throw inputValuesResult.error
      }

      const inputValues = inputValuesResult.value

      const filteredPrimaryIndices = primaryIndices.filter(
        (i) => outputRange[i] !== NO_OUTPUT
      )

      const overlapCheck = await ResultAsync.fromPromise(
        dataMatchInputOutputOverlapCheck(inputRange, outputRange),
        (error) =>
          new Error(`Overlap check failed, error: ${errorStringify(error)}`)
      )

      if (overlapCheck.isErr()) {
        throw overlapCheck.error
      }

      const pass = overlapCheck.value

      if (!pass.pass) {
        toast.error(pass.message, {})
        return
      }

      const fetchFilesStart = dayjs()

      const filesResult = await ResultAsync.fromPromise(
        getInputFiles(files, isLocalMode),
        (error) =>
          new Error(`Failed to fetch files, error: ${errorStringify(error)}`)
      )

      if (filesResult.isErr()) {
        throw filesResult.error
      }

      const inputFiles = filesResult.value

      const fetchFileEnd = dayjs()

      if (inputFiles.length < 1) {
        toast.error('Please upload or select at least one file', {})
        return
      }

      console.log(
        'Fetch files takes:',
        fetchFileEnd.diff(fetchFilesStart, 'millisecond')
      )

      // terminate if data match operation is canceled by users
      if (dataMatchIsCanceled.current) {
        toast.warn('⚠️Terminating...')
        return
      }
      const dataMatchStart = dayjs()
      const dataMatchResult = dataMatchWithDuplicateHandling(
        inputFiles,
        inputValues.textResult,
        inputValues.valueResult,
        filteredPrimaryIndices,
        firstRowHeader
        // isLocalMode
      )

      const dataMatchEnd = dayjs()
      console.log(
        'data match takes:',
        dataMatchEnd.diff(dataMatchStart, 'millisecond')
      )

      // if (dataMatchResult.isErr()) {
      //   throw dataMatchResult.error
      // }

      const result = dataMatchResult

      const arr = formatDataMatchResultWithDuplicateHandling(result)
      const filteredOutputRange = outputRange.filter((r) => r !== NO_OUTPUT)

      // terminate if data match operation is canceled by users
      if (dataMatchIsCanceled.current) {
        toast.warn('⚠️Terminating...')
        return
      }
      return Excel.run(async (ctx) => {
        const disableEventsResult = await ResultAsync.fromPromise(
          disableEvents(ctx),
          (error) => new Error(`disableEvents error: ${errorStringify(error)}`)
        )

        if (disableEventsResult.isErr()) {
          throw disableEventsResult.error
        }

        const sheetResult = await getActiveWorksheet(ctx, ['id', 'name'])
        if (sheetResult.isErr()) {
          throw sheetResult.error
        }
        const sheet = sheetResult.value

        const iRangeResult = await getRangeByWorksheet(ctx, sheet, inputRange, [
          'rowCount',
          'rowIndex',
        ])
        if (iRangeResult.isErr()) {
          throw iRangeResult.error
        }
        const iRange = iRangeResult.value

        const outputRangesResult = await getDataMatchOutputRanges(
          ctx,
          arr,
          filteredOutputRange,
          iRange,
          sheet
        )
        if (outputRangesResult.isErr()) {
          throw outputRangesResult.error
        }
        const outputRanges = outputRangesResult.value

        if (firstRowHeader) {
          for (let i = 0; i < outputRanges[0].length; i++) {
            if (outputRanges[0][i].values[0][0] !== '') {
              arr[0][i] = outputRanges[0][i].values[0][0]
            } else if (outputRanges[0][i].values[0][0] === '') {
              outputRanges[0][i].values = [[arr[0][i]]]
            }
            if (outputRanges[0][i].values[0][0] === '') {
              outputRanges[0][i].values = [[arr[0][i]]]
            }
            if (outputRanges[0][i].values[0][0] === '') {
              outputRanges[0][i].values = [[arr[0][i]]]
            }
          }
        }

        const rects = getDataMatchReferenceRectsV1({
          formattedResult: arr,
          outputRanges,
          result,
          worksheet: sheet,
          textResult: inputValues.textResult,
        })

        // terminate if data match operation is canceled by users
        if (dataMatchIsCanceled.current) {
          toast.warn('⚠️Terminating...')
          return
        }

        const createBindingsResult = await createDataMatchBindings(
          ctx,
          rects,
          sheet
        )
        if (createBindingsResult.isErr()) {
          throw createBindingsResult.error
        }

        const metricResult = await ResultAsync.fromPromise(
          addMetric({
            namespace: MetricNamespace.DATA_MATCH,
            metric_value: 1,
            metric_name: MetricName.COUNT,
          }),
          (error) =>
            new Error(
              `Failed to add data match metric: ${errorStringify(error)}`
            )
        )

        metricResult.mapErr((err) => {
          toast.error(err.message)
        })

        for (let i = 0; i < arr.length; i++) {
          if (firstRowHeader && i === 0) continue
          for (let j = 0; j < arr[i].length; j++) {
            const range = outputRanges[i][j]
            const processedValue = processLargeNumberValueAsText(arr[i][j])
            setOuputRangeStyle(range, processedValue)
          }
        }

        const syncResult = await ResultAsync.fromPromise(
          ctx.sync(),
          (error) =>
            new Error(`Excel request sync error ${errorStringify(error)}`)
        )

        if (syncResult.isErr()) {
          throw syncResult.error
        }

        const addMultipleReferencesResult = await ResultAsync.fromPromise(
          dispatch(addMultipleReferences(rects)),
          (error) =>
            new Error(
              `addMultipleReferencesResult error: ${errorStringify(error)}`
            )
        )

        if (addMultipleReferencesResult.isErr()) {
          throw addMultipleReferencesResult.error
        }
      })
    }
    const start = dayjs()
    func()
      .catch((err) => {
        console.error(errorStringify(err))
        toast.error(`⚠️Data match Error: ${errorStringify(err)}`)
      })
      .finally(async () => {
        const end = dayjs()
        console.log('time:', end.diff(start, 'ms'))
        const enableEventsResult = await ResultAsync.fromPromise(
          enableEvents(),
          (error) => new Error(`enableEvents error: ${errorStringify(error)}`)
        )

        enableEventsResult.mapErr((error) => {
          toast.error(
            `⚠️Failed to enable events, please reload the addin or resfresh the page. Error=${error}`
          )
        })

        dataMatchIsCanceled.current = false
        setLoading(false)
      })
  }

  const onMatchFolderClick = async () => {
    if (!selectedFolders.length) return

    const dataMatchFolderStructsResult = await ResultAsync.fromPromise(
      getDataMatchFolderStruct({
        data,
        folders,
        inputRange,
        outputRange,
        selectedFolders,
      }),
      (error) =>
        new Error(`Unable to get folder, error: ${errorStringify(error)}`)
    )

    if (dataMatchFolderStructsResult.isErr()) {
      throw dataMatchFolderStructsResult.error
    }

    const dataMatchFolderStructs = dataMatchFolderStructsResult.value

    // terminate data match if it is canceled by users
    if (dataMatchIsCanceled.current) {
      toast.warn('⚠️Terminating...')
      return
    }

    for (const struct of dataMatchFolderStructs) {
      const overlapCheckResult = await ResultAsync.fromPromise(
        dataMatchInputOutputOverlapCheck(inputRange, struct.outputColumns),
        (error) =>
          new Error(
            `Output overlap check failed, error: ${errorStringify(error)}`
          )
      )

      if (overlapCheckResult.isErr()) {
        throw overlapCheckResult.error
      }

      const pass = overlapCheckResult.value

      if (!pass.pass) {
        toast.error(pass.message, {})
        return
      }

      const inputResult = await ResultAsync.fromPromise(
        loadTextFromRange(inputRange, struct.outputColumns),
        (error) =>
          new Error(
            `Failed to read input values, error: ${errorStringify(error)}`
          )
      )

      if (inputResult.isErr()) {
        throw inputResult.error
      }

      const inputValues = inputResult.value

      const filesResult = await ResultAsync.fromPromise(
        getInputFiles(
          files.filter((file) => struct.fileIds.includes(file.id)),
          isLocalMode
        ),
        (error) =>
          new Error(
            `Failed to get input files, error: ${errorStringify(error)}`
          )
      )

      if (filesResult.isErr()) {
        throw filesResult.error
      }

      const inputFiles = filesResult.value

      if (inputFiles.length < 1) {
        toast.error('Please upload or select at least one file', {})
        return
      }

      const filteredPrimaryIndices = primaryIndices.filter(
        (i) => outputRange[i] !== NO_OUTPUT
      )

      const dataMatchResult = dataMatchWithDuplicateHandling(
        inputFiles,
        inputValues.textResult,
        inputValues.valueResult,
        filteredPrimaryIndices,
        firstRowHeader
        // isLocalMode
      )

      // if (dataMatchResult.isErr()) {
      //   throw dataMatchResult.error
      // }

      const result = dataMatchResult

      struct.result = result

      const arr = formatDataMatchResultWithDuplicateHandling(result)

      struct.formattedResult = arr
      struct.textResult = inputValues.textResult
      struct.numberFormat = inputValues.numberFormat
    }
    // terminate data match if it is canceled by users
    if (dataMatchIsCanceled.current) {
      toast.warn('⚠️Terminating...')
      return
    }

    await Excel.run(async (ctx) => {
      const disableEventsResult = await ResultAsync.fromPromise(
        disableEvents(ctx),
        (error) => new Error(`disableEvents error: ${errorStringify(error)}`)
      )

      if (disableEventsResult.isErr()) {
        throw disableEventsResult.error
      }

      const sheetResult = await getActiveWorksheet(ctx, ['id', 'name'])
      if (sheetResult.isErr()) {
        throw sheetResult.error
      }
      const sheet = sheetResult.value

      const iRangeResult = await getRangeByWorksheet(ctx, sheet, inputRange, [
        'rowCount',
        'rowIndex',
      ])
      if (iRangeResult.isErr()) {
        throw iRangeResult.error
      }
      const iRange = iRangeResult.value

      // terminate data match if it is canceled by users
      if (dataMatchIsCanceled.current) {
        toast.warn('⚠️Terminating...')
        return
      }
      for (const struct of dataMatchFolderStructs) {
        const filteredOutputRange = struct.outputColumns.filter(
          (r) => r !== NO_OUTPUT
        )

        const outputRangesResult = await getDataMatchOutputRanges(
          ctx,
          struct.formattedResult,
          filteredOutputRange,
          iRange,
          sheet
        )
        if (outputRangesResult.isErr()) {
          throw outputRangesResult.error
        }
        const outputRanges = outputRangesResult.value

        if (firstRowHeader) {
          for (let i = 0; i < outputRanges[0].length; i++) {
            if (outputRanges[0][i].values[0][0] !== '') {
              struct.formattedResult[0][i] = outputRanges[0][i].values[0][0]
            } else if (outputRanges[0][i].values[0][0] === '') {
              outputRanges[0][i].values = [[struct.formattedResult[0][i]]]
            }
          }
        }

        struct.excelRanges = outputRanges

        const rects = getDataMatchReferenceRectsV1({
          formattedResult: struct.formattedResult,
          outputRanges,
          result: struct.result,
          worksheet: sheet,
          textResult: struct.textResult,
        })

        struct.rects = rects

        const createBindingsResult = await createDataMatchBindings(
          ctx,
          struct.rects ?? [],
          sheet
        )
        if (createBindingsResult.isErr()) {
          throw createBindingsResult.error
        }
      }

      for (const strcut of dataMatchFolderStructs) {
        for (let i = 0; i < strcut.formattedResult.length; i++) {
          if (firstRowHeader && i === 0) continue
          for (let j = 0; j < strcut.formattedResult[i].length; j++) {
            const range = strcut.excelRanges[i][j]
            setOuputRangeStyle(range, strcut.formattedResult[i][j])
          }
        }
      }

      const syncRersult = await ResultAsync.fromPromise(
        ctx.sync(),
        (error) =>
          new Error(`Excel request sync error ${errorStringify(error)}`)
      )

      if (syncRersult.isErr()) {
        throw syncRersult.error
      }

      for (const strcut of dataMatchFolderStructs) {
        const addMultipleReferencesResult = await ResultAsync.fromPromise(
          dispatch(addMultipleReferences(strcut.rects ?? [])),
          (error) =>
            new Error(
              `addMultipleReferencesResult error: ${errorStringify(error)}`
            )
        )

        if (addMultipleReferencesResult.isErr()) {
          throw addMultipleReferencesResult.error
        }
      }

      const metricResult = await ResultAsync.fromPromise(
        addMetric({
          namespace: MetricNamespace.DATA_MATCH,
          metric_value: 1,
          metric_name: MetricName.COUNT,
        }),
        (error) =>
          new Error(`Failed to add data match metric: ${errorStringify(error)}`)
      )

      metricResult.mapErr((err) => {
        toast.error(err.message)
      })
    })
  }

  const onMatchFolderClickWrapper = () => {
    setLoading(true)
    dataMatchIsCanceled.current = false
    onMatchFolderClick()
      .catch((err) => {
        console.error(`onMatchFolderClick error=${errorStringify(err)}`)
        toast.error(`⚠️Data match Error: ${errorStringify(err)}`)
      })
      .finally(async () => {
        const enableEventsResult = await ResultAsync.fromPromise(
          enableEvents(),
          (error) => new Error(`enableEvents error: ${errorStringify(error)}`)
        )

        enableEventsResult.mapErr((error) => {
          toast.error(
            `⚠️Failed to enable events, please reload the addin or resfresh the page. Error=${errorStringify(
              error
            )}`
          )
        })
        dataMatchIsCanceled.current = false

        setLoading(false)
      })
  }

  const onMatchClick = () => {
    if (searchMode === 1) {
      onMatchDocumentClick()
    } else if (searchMode === 2) {
      onMatchFolderClickWrapper()
    }
  }

  const selectRange = (range: string) =>
    Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet()
      const r = sheet.getRange(range)
      r.select()
      await ctx.sync()
    })

  const renderErrorTitle = () => {
    // if (isLocalMode && localError)
    //   return getOfficeErrorTitle(localError.message)
    return isOfficeError(error)
      ? getOfficeErrorTitle(error.message)
      : 'Internal Error'
  }

  const allowStepTwo = () => {
    if (inputRange) {
      dispatchDataMatchPageStateRef.current({
        type: 'BATCH',
        payload: {
          inputRangeFocus: false,
          stepper: Stepper.FILE_SELECTION,
        },
      })
    } else toast.error('Please select input')
  }

  const allowStepThree = () => {
    const count = files.reduce((a, b) => {
      if (b.checked) return a + 1
      return a
    }, 0)
    if (count > 0) {
      dispatchDataMatchPageStateRef.current({
        type: 'SET_STEP',
        payload: Stepper.OUTPUT_SELECTION,
      })
    } else toast.error('Please select or upload at least one file')
  }

  return (
    <motion.div
      // initial={{ scaleX: 0 }}
      // animate={{ scaleX: 1, transition: { duration: 0.5, ease: 'circOut' } }}
      // exit={{ scaleX: 1, transition: { duration: 0.5, ease: 'circIn' } }}
      // style={{ originX: isPresent ? 0 : 1 }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: 0.3,
        ease: [0, 0.71, 0.2, 1.01],
      }}
      className="h-full"
    >
      <div className="flex flex-col h-full w-full bg-[#f5f5f5] overflow-y-auto p-4 sm:p-8">
        {loading && !isError && (
          <div className="flex flex-col justify-center items-center h-full w-full gap-2">
            <div>
              <ClockLoader size={50} color="#36d7b7" />
            </div>
            <div className="font-semibold text-lg">Data match in progress </div>
            <div>
              <button
                className={`bg-slate-200 p-2 text-md font-medium hover:bg-slate-300 rounded cursor-pointer`}
                onClick={() => {
                  dataMatchIsCanceled.current = true
                  setLoading(false)
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {!loading && (
          <>
            <ol className="relative border-s-2 hidden sm:block">
              <li className="mb-10 ms-4">
                <span className="absolute flex items-center justify-center w-5 h-5 bg-green-50  -start-3 ">
                  {stepper > 0 && (
                    <CheckOutlinedIcon sx={{ fontSize: 18, color: 'gray' }} />
                  )}
                </span>
                <CustomDisclosure
                  header="1. Select input range."
                  element={
                    <InputRangeElem
                      allowStepTwo={allowStepTwo}
                      firstRowHeader={firstRowHeader}
                      inputRange={inputRange}
                      inputRangeRef={inputRangeRef}
                      onInputRangeChange={onInputRangeChange}
                      setFirstRowHeader={setFirstRowHeader}
                    />
                  }
                  open={stepper === 0 ? true : false}
                  defaultOpen={true}
                />
              </li>
              <li className="mb-10 ms-4">
                <span className="absolute flex items-center justify-center w-5 h-5 bg-green-50  -start-3 ">
                  {stepper > 1 && (
                    <CheckOutlinedIcon sx={{ fontSize: 18, color: 'gray' }} />
                  )}
                </span>
                <CustomDisclosure
                  element={
                    <FilesElem
                      allowStepThree={allowStepThree}
                      checkAllFiles={allFilesSelected}
                      fileStructure={fileStructure}
                      files={files}
                      folders={folders}
                      isError={isError}
                      isFetching={isFetching}
                      isLoading={isLoading}
                      navigate={navigate}
                      onCheckAllFilesChange={onCheckAllFilesChange}
                      refetch={refetch}
                      renderErrorTitle={renderErrorTitle}
                      setFiles={setFiles}
                      setInputRangeFocus={setInputRangeFocus}
                      setStepper={setStepper}
                      onSingleFileCheck={onSingleFileCheck}
                      setSelectedFolders={setSelectedFolders}
                      selectedFolders={selectedFolders}
                      searchMode={searchMode}
                      setSearchMode={setSearchMode}
                      uncheckAllFiles={uncheckAllFiles}
                      foldersAreAllEmpty={foldersAreAllEmpty}
                    />
                  }
                  header="2. Select supporting documents or folders if they belong to different types."
                  open={stepper === 1 ? true : false}
                  defaultOpen={true}
                />
              </li>
              <li className="ms-4">
                <span className="absolute flex items-center justify-center w-5 h-5 bg-green-50  -start-3 ">
                  {/* <CheckOutlinedIcon sx={{ fontSize: 18, color: 'gray' }} /> */}
                </span>
                <CustomDisclosure
                  header="3. Select output range."
                  element={
                    <OutputElem
                      getInitialOutputCols={getInitialOutputCols}
                      outputTableError={outputTableError}
                      setOutputTableError={setOutputTableError}
                      setStepper={setStepper}
                      table={table}
                      onMatchClick={onMatchClick}
                    />
                  }
                  open={stepper === 2 ? true : false}
                  defaultOpen={true}
                />
              </li>
            </ol>
            <div className="flex flex-col w-full sm:hidden gap-4 p-2">
              <div>
                <CustomDisclosure
                  header="1. Select input range."
                  element={
                    <InputRangeElem
                      allowStepTwo={allowStepTwo}
                      firstRowHeader={firstRowHeader}
                      inputRange={inputRange}
                      inputRangeRef={inputRangeRef}
                      onInputRangeChange={onInputRangeChange}
                      setFirstRowHeader={setFirstRowHeader}
                    />
                  }
                  open={stepper === 0 ? true : false}
                  defaultOpen={true}
                />
              </div>
              <div>
                <CustomDisclosure
                  element={
                    <FilesElem
                      allowStepThree={allowStepThree}
                      checkAllFiles={allFilesSelected}
                      fileStructure={fileStructure}
                      files={files}
                      folders={folders}
                      isError={isError}
                      isFetching={isFetching}
                      isLoading={isLoading}
                      navigate={navigate}
                      onCheckAllFilesChange={onCheckAllFilesChange}
                      refetch={refetch}
                      renderErrorTitle={renderErrorTitle}
                      setFiles={setFiles}
                      setInputRangeFocus={setInputRangeFocus}
                      setStepper={setStepper}
                      onSingleFileCheck={onSingleFileCheck}
                      setSelectedFolders={setSelectedFolders}
                      selectedFolders={selectedFolders}
                      searchMode={searchMode}
                      setSearchMode={setSearchMode}
                      uncheckAllFiles={uncheckAllFiles}
                      foldersAreAllEmpty={foldersAreAllEmpty}
                    />
                  }
                  header="2. Select supporting documents or folders if they belong to different types."
                  open={stepper === 1 ? true : false}
                  defaultOpen={true}
                />
              </div>
              <div>
                <CustomDisclosure
                  header="3. Select output range."
                  element={
                    <OutputElem
                      getInitialOutputCols={getInitialOutputCols}
                      outputTableError={outputTableError}
                      setOutputTableError={setOutputTableError}
                      setStepper={setStepper}
                      table={table}
                      onMatchClick={onMatchClick}
                    />
                  }
                  open={stepper === 2 ? true : false}
                  defaultOpen={true}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  )
}

export default withDataMatchPageStateContext(DataMatching)
